import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useCollectionBinding,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { amenityIcon } from '../utils/amenityIcon';
import { btnPrimary, clean, hasValue, linkTarget } from '../utils/content';

interface Amenity {
  title?: string | null;
  is_featured?: string | null;
}

const photoOneDef: SlotGroupDefinition = {
  label: 'Amenity Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 7, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo of the strongest shared amenity space here (fitness, lounge, pool, coworking — whatever this property actually has).',
    context: 'Upper photo of two stacked beside the home page amenities highlight. Must differ from photo 2.',
  },
};

const photoTwoDef: SlotGroupDefinition = {
  label: 'Amenity Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 8, fieldMappings: { image: 'image', alt: 'alt_text' } },
  generator: {
    prompt: 'A photo of a different amenity or common area than photo 1.',
    context: 'Lower photo of two stacked beside the home page amenities highlight. Different space from photo 1.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Amenities Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/features' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function AmenitiesHighlight() {
  const heading = useContentToken('amenitieshighlight.heading');
  const photoOne = useSlotGroup('amenitieshighlight.photo_1', photoOneDef);
  const photoTwo = useSlotGroup('amenitieshighlight.photo_2', photoTwoDef);
  const cta = useSlotGroup('amenitieshighlight.cta', ctaDef);
  const items = useCollectionBinding<Amenity>('amenitieshighlight.items', { collection: 'amenities', defaultLimit: 6 });

  const list = (items || []).filter((a) => hasValue(a.title));

  return (
    <section className="bg-accent text-[color:var(--vibe-color-text-on-secondary)]">
      <div className="mx-auto grid max-w-[1180px] gap-10 px-6 py-14 md:py-16 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:gap-14 lg:px-10">
        <div>
          <EditableToken path="amenitieshighlight.heading" as="h2" className="js-reveal js-heading text-[30px] sm:text-[35px]">
            {heading}
          </EditableToken>
          <RichTextContent path="amenitieshighlight.body" as="div" className="js-reveal js-d1 js-copy mt-6" />

          <EditableCollectionBinding bindingPath="amenitieshighlight.items" label="Highlighted Amenities">
            {list.length > 0 && (
              <ul className="js-stagger js-d2 mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                {list.map((a, i) => {
                  const Icon = amenityIcon(a.title);
                  return (
                    <li key={`${a.title}-${i}`} className="group flex items-center gap-4 font-body text-[17px]">
                      <Icon className="h-6 w-6 shrink-0 stroke-[1.5] transition-transform duration-500 ease-out group-hover:-translate-y-0.5 group-hover:scale-110" aria-hidden />
                      <span>{clean(a.title)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </EditableCollectionBinding>

          <EditableSlotGroup groupPath="amenitieshighlight.cta" definition={ctaDef} className="js-reveal js-d3">
            <a href={cta.url || '/features'} target={linkTarget(cta.url, cta._target)} className={`${btnPrimary} mt-9`}>
              {cta.label || 'View All Amenities'}
            </a>
          </EditableSlotGroup>
        </div>

        <div className="grid grid-cols-2 gap-4 self-start lg:grid-cols-1 lg:gap-6">
          <EditableSlotGroup groupPath="amenitieshighlight.photo_1" definition={photoOneDef}>
            <div className="js-reveal-img js-zoom js-d1 aspect-[4/3] w-full overflow-hidden bg-muted">
              <VibesitesImage
                url={photoOne.image || ''}
                alt={hasValue(photoOne.alt) ? photoOne.alt : ''}
                sizes="(min-width: 1024px) 34vw, 50vw"
                className="h-full w-full object-cover"
              />
            </div>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="amenitieshighlight.photo_2" definition={photoTwoDef}>
            <div className="js-reveal-img js-zoom js-d3 aspect-[4/3] w-full overflow-hidden bg-muted">
              <VibesitesImage
                url={photoTwo.image || ''}
                alt={hasValue(photoTwo.alt) ? photoTwo.alt : ''}
                sizes="(min-width: 1024px) 34vw, 50vw"
                className="h-full w-full object-cover"
              />
            </div>
          </EditableSlotGroup>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesHighlight',
  displayName: 'Amenities Highlight',
  tokens: {
    'amenitieshighlight.heading': {
      label: 'Heading',
      type: 'text',
      defaultValue: 'Modern Elegance, at Your Convenience',
    },
    'amenitieshighlight.body': {
      label: 'Body',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      suggestedFields: ['property_home_amenities_blurb', 'property_amenities_blurb'],
      defaultValue:
        '<p>From thoughtfully designed shared spaces to the everyday conveniences that make life easier, <span data-type="field-token" data-field="property_name"></span> is built around the way you live.</p>',
      generator: {
        prompt: 'A short, inviting overview of the shared amenities, naming a few real ones with <strong> emphasis. Point readers to the amenities page if it exists.',
        context: 'Home page amenities band, beside two amenity photos and above a six-item amenity icon list.',
        length: '3–5 sentences',
      },
    },
  },
  slotGroups: {
    'amenitieshighlight.photo_1': photoOneDef,
    'amenitieshighlight.photo_2': photoTwoDef,
    'amenitieshighlight.cta': ctaDef,
  },
  collectionBindings: {
    'amenitieshighlight.items': {
      label: 'Highlighted Amenities',
      collection: 'amenities',
      defaultLimit: 6,
      description: 'Up to six amenities listed with icons. Reorder or hide items to pick the highlights.',
    },
  },
};

(AmenitiesHighlight as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesHighlight;
